/**
 * Clock - Isolierte Uhr-Komponente
 *
 * Re-rendert nur sich selbst, nicht die Parent-Komponente.
 * Verhindert Flackern/Fokusverlust in Formularen.
 *
 * VS#16: Schichtanzeige nutzt jetzt Backend-API statt lokaler Logik.
 */

import { useState, useEffect, memo } from 'react';
import { getCurrentShift, CurrentShiftResponse } from '../api/client';

interface ClockProps {
  showDate?: boolean;
  className?: string;
}

export const Clock = memo(function Clock({ showDate = true, className = '' }: ClockProps) {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={`text-right ${className}`}>
      <div className="text-2xl font-mono font-bold text-tinte-2">
        {time.toLocaleTimeString('de-DE', {
          timeZone: 'Europe/Berlin',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
      {showDate && (
        <div className="text-xs text-tinte-3">
          {time.toLocaleDateString('de-DE', {
            timeZone: 'Europe/Berlin',
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </div>
      )}
    </div>
  );
});

/**
 * DEPRECATED: Lokale Schichtberechnung
 * Nutze stattdessen useCurrentShift() Hook oder getCurrentShift() API.
 *
 * Diese Funktion bleibt für Abwärtskompatibilität, aber die Werte
 * weichen möglicherweise vom Backend ab. Für korrekte Werte immer
 * die API verwenden.
 */
export function getActiveShiftNow(): {
  name: string;
  isActive: boolean;
  shiftType: 'EARLY' | 'LATE' | 'SATURDAY' | null;
} {
  console.warn(
    '[DEPRECATED] getActiveShiftNow() - Nutze useCurrentShift() oder getCurrentShift() API',
  );
  return { name: 'Laden...', isActive: false, shiftType: null };
}

/**
 * Hook für aktuelle Schicht (Backend-basiert)
 *
 * @returns { data, loading, error, refetch }
 */
export function useCurrentShift() {
  const [data, setData] = useState<CurrentShiftResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCurrentShift();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Schicht');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
    // Alle 60 Sekunden aktualisieren
    const timer = setInterval(refetch, 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  return { data, loading, error, refetch };
}

/**
 * ShiftDisplay - Zeigt die aktuelle Schicht an (Backend-basiert)
 *
 * VS#16: Eine Quelle der Wahrheit - Backend bestimmt Schicht
 */
export const ShiftDisplay = memo(function ShiftDisplay({ className = '' }: { className?: string }) {
  const { data, loading, error } = useCurrentShift();

  if (loading) {
    return <span className={className}>Laden...</span>;
  }

  if (error) {
    return <span className={`${className} text-stoer`}>Fehler</span>;
  }

  if (!data?.hasActiveShift) {
    return <span className={className}>Keine aktive Schicht</span>;
  }

  return <span className={className}>{data.ruleName}</span>;
});
